import { Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { ToastrService } from 'ngx-toastr';
import { saveAs } from 'file-saver';
import { Annuncio } from 'src/app/models/annuncio';
import { AnnuncioAllegato } from 'src/app/models/annuncio-allegato';
import { ConfirmDialogComponent } from 'src/app/material-module/confirm-dialog/confirm-dialog.component';
import { AnnuncioService } from 'src/app/services/annuncio.service';

@Component({
  selector: 'annuncio-allegati-dialog',
  templateUrl: './annuncio-allegati.component.html',
  styleUrls: ['./annuncio-allegati.component.scss']
})
export class AnnuncioAllegatiDialogComponent implements OnInit {

  annuncio: Annuncio;
  allegati: AnnuncioAllegato[] = [];
  loading = false;
  uploading = false;

  @ViewChild('table') table: MatTable<AnnuncioAllegato>;

  constructor(
    private annuncioService: AnnuncioService,
    private toast: ToastrService,
    private dialog: MatDialog,
    @Optional() public dialogRef: MatDialogRef<AnnuncioAllegatiDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { annuncio: Annuncio },
  ) {
    this.annuncio = data?.annuncio;
  }

  ngOnInit(): void {
    this.loadAllegati();
  }

  async loadAllegati() {
    if (!this.annuncio?.id) {
      return;
    }
    this.loading = true;
    try {
      this.allegati = await this.annuncioService.getAllegati(this.annuncio.id);
    } catch (error) {
      console.error(error);
      this.toast.error('Impossibile caricare gli allegati.', 'Errore');
    } finally {
      this.loading = false;
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file || !this.annuncio?.id) {
      return;
    }

    this.uploading = true;
    this.annuncioService.uploadAllegato(this.annuncio.id, file).subscribe({
      next: allegato => {
        this.toast.success('Allegato caricato con successo.');
        this.allegati = [allegato, ...this.allegati];
        this.table?.renderRows();
      },
      error: err => {
        console.error(err);
        this.toast.error('Caricamento allegato fallito.', 'Errore');
      },
      complete: () => {
      this.uploading = false;
      if (input) {
        input.value = '';
      }
    }
    });
  }

  openVideo(allegato: AnnuncioAllegato) {
    if (!allegato?.isVideo) {
      this.openDownload(allegato);
      return;
    }

    if (!this.annuncio?.id || !allegato?.id) {
      return;
    }

    this.annuncioService.downloadAllegato(this.annuncio.id, allegato.id).subscribe({
      next: response => {
        const blob = response.body;
        if (!blob) {
          this.toast.error('Riproduzione video fallita.', 'Errore');
          return;
        }
        const objectUrl = window.URL.createObjectURL(blob);
        window.open(objectUrl, '_blank', 'noopener');
        setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
      },
      error: err => {
        console.error(err);
        this.toast.error('Riproduzione video fallita.', 'Errore');
      }
    });
  }

  openDownload(allegato: AnnuncioAllegato) {
    if (!this.annuncio?.id || !allegato?.id) {
      return;
    }

    this.annuncioService.downloadAllegato(this.annuncio.id, allegato.id).subscribe({
      next: response => {
        const blob = response.body;
        if (!blob) {
          this.toast.error('Download allegato fallito.', 'Errore');
          return;
        }
        const filename = allegato.nome_originale || 'allegato';
        saveAs(blob, filename);
      },
      error: err => {
        console.error(err);
        this.toast.error('Download allegato fallito.', 'Errore');
      }
    });
  }

  deleteAllegato(allegato: AnnuncioAllegato) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        message: `Sei sicuro di voler eliminare l'allegato ${allegato.humanIdentifier}?`
      }
    });

    dialogRef.afterClosed().subscribe(async confirmed => {
      if (!confirmed) {
        return;
      }
      try {
        await this.annuncioService.deleteAllegato(this.annuncio.id, allegato.id);
        this.toast.success('Allegato eliminato.');
        this.allegati = this.allegati.filter(a => a.id !== allegato.id);
        this.table?.renderRows();
      } catch (error) {
        console.error(error);
        this.toast.error('Eliminazione allegato fallita.', 'Errore');
      }
    });
  }
}
